"use client";

import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  index: "01",
  title: "OTA REVENUE MANAGEMENT",
  price: "CUSTOM QUOTE",
  description: "Strategic management of your OTA presence to maximize visibility and conversion. We optimize your listings, manage rate parity, and implement data-driven pricing strategies to ensure your property captures the highest possible demand.",
  specs: ["OTA listing optimization", "Rate parity monitoring", "Conversion rate analysis", "Competitor benchmarking"]
}, {
  index: "02",
  title: "HOSPITALITY TECH IMPLEMENTATION",
  price: "CUSTOM QUOTE",
  description: "Expert guidance on selecting and integrating the right technology stack for your hotel. From PMS to channel managers and booking engines, we ensure your systems communicate seamlessly to drive operational efficiency.",
  specs: ["Tech stack audit", "Vendor selection support", "System integration", "Workflow automation"]
}, {
  index: "03",
  title: "REVENUE AUDITS",
  price: "COMPLIMENTARY",
  description: "A comprehensive deep-dive into your current revenue performance. We identify missed opportunities, analyze your distribution channels, and provide actionable insights to boost your bottom line immediately.",
  specs: ["Revenue leakage analysis", "Distribution channel audit", "Pricing strategy review", "Actionable growth roadmap"]
}, {
  index: "04",
  title: "DIRECT BOOKING GROWTH",
  price: "CUSTOM QUOTE",
  description: "Shift your dependency away from high-commission OTAs. We implement digital marketing and website optimization strategies designed to capture more direct bookings and increase your net revenue per available room.",
  specs: ["Booking engine optimization", "Direct channel marketing", "Loyalty program strategy", "Conversion funnel design"]
}, {
  index: "05",
  title: "DYNAMIC PRICING & FORECASTING",
  price: "CUSTOM QUOTE",
  description: "Leverage real-time market data to adjust your rates dynamically. Our forecasting models help you anticipate demand shifts, ensuring your pricing is always optimized for maximum occupancy and ADR.",
  specs: ["Demand forecasting", "Dynamic rate adjustment", "Market trend analysis", "Seasonality planning"]
}, {
  index: "06",
  title: "REMOTE REVENUE MANAGEMENT",
  price: "MONTHLY RETAINER",
  description: "Outsource your revenue management to an expert. We act as your dedicated remote revenue manager, handling daily rate updates, inventory management, and strategic reporting to keep your property competitive.",
  specs: ["Daily rate management", "Inventory control", "Performance reporting", "Strategic advisory"]
}, {
  index: "07",
  title: "CHANNEL MANAGER SETUP",
  price: "CUSTOM QUOTE",
  description: "Ensure your inventory is perfectly synced across all platforms. We configure your channel manager to prevent overbookings and ensure real-time rate updates across your entire distribution network.",
  specs: ["Channel mapping", "Overbooking prevention", "Real-time sync setup", "Distribution strategy"]
}, {
  index: "08",
  title: "REVENUE INTELLIGENCE",
  price: "CUSTOM QUOTE",
  description: "Advanced analytics and reporting to track your hotel's health. We transform complex data into clear, actionable intelligence, helping you make informed decisions that drive long-term revenue growth.",
  specs: ["Custom KPI dashboards", "Revenue trend analysis", "Market share reporting", "Strategic planning"]
}];
export default function Services() {
  const [openItem, setOpenItem] = useState<string>("item-0");
  const hero = useRevealOnScroll<HTMLDivElement>();
  const list = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="4320" id="servicenavigation" className="bg-background text-foreground relative overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-6 pt-32 pb-28 md:px-12 md:pt-44 md:pb-36">
        <Breadcrumb className="mb-16 md:mb-24">
          <BreadcrumbList className="font-default text-xs uppercase tracking-widest text-foreground/50">
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/" className="hover:text-foreground transition-colors">
                  /
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-foreground/30">
              <span>/</span>
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/OurServicesPage" className="hover:text-foreground transition-colors">
                  SERVICES
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-foreground/30">
              <span>/</span>
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage className="text-foreground">
                REVENUE SOLUTIONS
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div ref={hero.ref} className={`grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12 transition-all duration-1000 ease-out ${hero.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h1 className="font-default lg:col-span-9 text-[64px] leading-[0.92] font-bold uppercase tracking-tight sm:text-[88px] md:text-[112px] lg:text-[140px]">
            REVENUE
            <br />
            <span className="font-default italic font-normal">STRATEGIES.</span>
          </h1>
          <div className="lg:col-span-3 flex items-end">
            <p className="font-default text-lg italic leading-snug text-foreground/70 md:text-xl">
              Professional revenue management consulting designed to maximize your hotel's profitability through data-driven insights and modern distribution strategies.
            </p>
          </div>
        </div>

        <div className="mt-24 md:mt-32 flex items-center justify-between border-t border-foreground/10 pt-6">
          <span className="font-default text-[11px] uppercase tracking-[0.2em] text-foreground/50">
            SERVICES 01-08
          </span>
          <span className="font-default text-[11px] uppercase tracking-[0.2em] text-foreground/50">
            HOSPITALITY CONSULTING
          </span>
        </div>

        <div ref={list.ref} className={`mt-2 transition-all duration-1000 delay-200 ease-out ${list.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Accordion type="single" collapsible value={openItem} onValueChange={v => setOpenItem(v)} className="w-full">
            {services.map((service, i) => {
            const value = `item-${i}`;
            const isOpen = openItem === value;
            return <AccordionItem key={service.index} data-index={i} value={value} className="border-b border-foreground/10 last:border-b-0 group">
                  <AccordionTrigger className={`hover:no-underline py-0 rounded-none transition-colors duration-500 [&>svg]:hidden ${isOpen ? "bg-foreground/[0.03]" : "hover:bg-foreground/[0.03]"}`}>
                    <div className="grid w-full grid-cols-12 items-center gap-4 min-h-[100px] md:min-h-[120px] px-2 md:px-4">
                      <div className="col-span-2 md:col-span-1">
                        <span className="font-default text-sm md:text-base text-foreground/40">
                          [{service.index}]
                        </span>
                      </div>
                      <div className="col-span-7 md:col-span-8">
                        <h3 className={`font-default text-xl sm:text-2xl md:text-3xl lg:text-[40px] leading-[1.02] font-bold uppercase tracking-tight transition-colors duration-500 ${isOpen ? "text-secondary" : "text-foreground"}`}>
                          {service.title}
                        </h3>
                      </div>
                      <div className="col-span-3 flex items-center justify-end gap-3 md:gap-6">
                        <span className="font-default text-[10px] md:text-xs uppercase tracking-widest text-secondary hidden sm:inline">
                          {service.price}
                        </span>
                        <span className={`inline-flex h-9 w-9 md:h-11 md:w-11 items-center justify-center border border-foreground/30 transition-transform duration-500 ${isOpen ? "rotate-45 border-secondary text-secondary" : "text-foreground/70"}`}>
                          <ArrowUpRight className="h-4 w-4 md:h-5 md:w-5" />
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-0 pb-0">
                    <div className="grid grid-cols-12 gap-6 md:gap-10 bg-foreground/[0.03] px-2 md:px-4 py-10 md:py-14">
                      <div className="col-span-12 md:col-span-1">
                        <span className="font-default text-[10px] uppercase tracking-widest text-foreground/40">
                          DETAIL
                        </span>
                      </div>
                      <div className="col-span-12 md:col-span-6">
                        <p className="font-default text-lg md:text-xl leading-relaxed text-foreground/80">
                          {service.description}
                        </p>
                        <span className="mt-6 inline-block font-default text-xs uppercase tracking-widest text-secondary sm:hidden">
                          {service.price}
                        </span>
                      </div>
                      <div className="col-span-12 md:col-span-5">
                        <ul className="space-y-3">
                          {service.specs.map((spec, j) => <li key={j} data-index={j} className="flex items-start gap-3 font-default text-xs uppercase tracking-wider text-foreground/70">
                              <span className="text-secondary mt-[2px]">
                                ◇
                              </span>
                              <span>{spec}</span>
                            </li>)}
                        </ul>
                        <div className="mt-10 flex justify-end">
                          <Button asChild variant="ghost" className="font-default text-xs uppercase tracking-widest text-secondary hover:bg-secondary hover:text-white h-11 px-6 border border-secondary/40 hover:border-secondary shadow-md">
                            <Link to="/ContactPage">
                              BOOK CONSULTATION
                              <ArrowUpRight className="ml-2 h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>;
          })}
          </Accordion>
        </div>
      </div>
    </section>;
}